import { useCallback, useRef, useState } from 'react';

type Key = string;

export type UseSelectionMapReturn<V> = ReturnType<typeof useSelectionMap<string, V>>;

export default function useSelectionMap<K extends Key, V>(initialState?: Record<K, V>) {
  const [, setState] = useState(0);
  const rerender = () => setState((i) => (i + 1) % 1_000_000);

  const { current: map } = useRef(new Map<string, V>(Object.entries(initialState ?? {})));

  const entries = Array.from(map);

  const add = useCallback(
    (k: K, v: V) => {
      map.set(k, v);
      rerender();
    },
    [map]
  );

  const remove = useCallback(
    (k: K) => {
      map.delete(k);
      rerender();
    },
    [map]
  );

  const toggle = useCallback(
    (entries: [K, V][]) => {
      entries.forEach(([_k, v]) => {
        const k = String(_k);
        if (map.has(k) && map.get(k) === v) {
          map.delete(k);
        } else {
          map.set(k, v);
        }
      });
      rerender();
    },
    [map]
  );

  return {
    map,
    entries,
    add,
    remove,
    toggle,
  };
}
